import { alpha } from '@mui/material/styles';

// Medikal Sektör Renk Paleti - v5 (KORUNUYOR — kullanıcı onayı: "renk düzeni iyi")
const colors = {
    teal: '#0891b2',        // Ana renk - Sağlık sektörü standartı
    tealDark: '#0e7490',    // Koyu teal - gradient/hover derinliği
    cyan: '#22d3ee',        // Açık teal
    indigo: '#6366f1',      // Premium mor tonları
    indigoDark: '#4f46e5',  // Koyu indigo - gradient/hover derinliği
    purple: '#818cf8',      // Açık mor
    green: '#10b981',       // Başarı/Onay - Sağlık yeşili
    greenLight: '#34d399',
    red: '#ef4444',         // Hata/Uyarı
    orange: '#f59e0b',      // Dikkat
    yellow: '#fbbf24',
    gray: '#64748b',
    gray2: '#94a3b8',
    gray3: '#cbd5e1',
    gray4: '#e2e8f0',
    gray5: '#f1f5f9',
    gray6: '#f8fafc',
};

const getGlassEffect = (mode) => ({
    background: mode === 'dark'
        ? 'rgba(30, 41, 59, 0.95)'
        : 'rgba(255, 255, 255, 0.98)', // Neredeyse tamamen opak - NET görünüm
    backdropFilter: 'blur(8px)', // Daha az blur
    WebkitBackdropFilter: 'blur(8px)',
    border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
    boxShadow: mode === 'dark'
        ? '0 4px 16px 0 rgba(0, 0, 0, 0.3)'
        : '0 4px 16px 0 rgba(0, 0, 0, 0.08)', // Daha hafif gölge
});

export const getDesignTokens = (mode) => ({
    palette: {
        mode,
        primary: {
            main: colors.teal,
            light: colors.cyan,
            dark: colors.tealDark,
        },
        secondary: {
            main: colors.indigo,
            light: colors.purple,
            dark: colors.indigoDark,
        },
        success: {
            main: colors.green,
            light: colors.greenLight,
        },
        error: {
            main: colors.red,
        },
        warning: {
            main: colors.orange,
        },
        background: {
            default: mode === 'dark' ? '#0f172a' : '#f8fafc', // Net arka plan renkleri
            paper: mode === 'dark' ? '#1e293b' : '#ffffff',
        },
        text: {
            primary: mode === 'dark' ? '#f8fafc' : '#1a1a1a', // SİYAH yazı
            secondary: mode === 'dark' ? '#cbd5e1' : '#4a5568',
        },
        divider: mode === 'dark' ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08),
    },
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
        h1: { fontWeight: 700 },
        h2: { fontWeight: 700 },
        h3: { fontWeight: 600 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600 }, // iOS buttons are not uppercase
    },
    shape: {
        borderRadius: 16, // iOS standard is pretty round
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    scrollbarColor: mode === 'dark' ? "#6b6b6b #2b2b2b" : "#959595 #f5f5f5",
                    "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                        backgroundColor: "transparent",
                        width: 8,
                    },
                    "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                        borderRadius: 8,
                        backgroundColor: mode === 'dark' ? "#6b6b6b" : "#959595",
                        minHeight: 24,
                    },
                    "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
                        backgroundColor: mode === 'dark' ? "#959595" : "#6b6b6b",
                    },
                    "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
                        backgroundColor: mode === 'dark' ? "#959595" : "#6b6b6b",
                    },
                    "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: mode === 'dark' ? "#959595" : "#6b6b6b",
                    },
                    "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
                        backgroundColor: "transparent",
                    },
                },
                // Erişilebilirlik: hareketi azalt tercihi olan kullanıcılarda
                // tüm geçiş/animasyonları neredeyse kapat (AĞIR animasyon yok kuralı).
                "@media (prefers-reduced-motion: reduce)": {
                    "*, *::before, *::after": {
                        animationDuration: "0.01ms !important",
                        animationIterationCount: "1 !important",
                        transitionDuration: "0.01ms !important",
                        scrollBehavior: "auto !important",
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    ...getGlassEffect(mode),
                    borderRadius: 20,
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: mode === 'dark'
                            ? '0 12px 40px 0 rgba(0, 0, 0, 0.45)'
                            : '0 12px 40px 0 rgba(31, 38, 135, 0.2)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    // Default paper shouldn't always be glass, but for this concept we make it default
                    // Override in specific cases if needed
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    padding: '10px 20px',
                    boxShadow: 'none',
                    // Hafif, sadece transform/opacity/gölge üzerinden geçiş (150-200ms).
                    transition: 'transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                    '&:active': {
                        transform: 'translateY(0)',
                    },
                    // Klavye erişilebilirliği: görünür focus halkası.
                    '&:focus-visible': {
                        outline: `2px solid ${alpha(colors.teal, 0.6)}`,
                        outlineOffset: 2,
                    },
                },
                // DİKKAT: gradient blanket `contained`'e DEĞİL, sadece primary/secondary'ye.
                // Aksi halde color="error"/"success"/"warning" contained butonlar da
                // teal olur (sil/onay butonlarının rengi bozulur).
                containedPrimary: {
                    background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealDark} 100%)`,
                    color: '#ffffff',
                    '&:hover': {
                        background: `linear-gradient(135deg, ${colors.tealDark} 0%, ${colors.tealDark} 100%)`,
                        boxShadow: `0 6px 16px ${alpha(colors.teal, 0.35)}`,
                        transform: 'translateY(-1px)',
                    },
                },
                containedSecondary: {
                    background: `linear-gradient(135deg, ${colors.indigo} 0%, ${colors.indigoDark} 100%)`,
                    color: '#ffffff',
                    '&:hover': {
                        background: `linear-gradient(135deg, ${colors.indigoDark} 0%, ${colors.indigoDark} 100%)`,
                        boxShadow: `0 6px 16px ${alpha(colors.indigo, 0.35)}`,
                        transform: 'translateY(-1px)',
                    },
                },
                // Renkli contained (error/success/warning) butonlarda da hafif yükselme.
                contained: {
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 16px ${alpha('#000000', 0.18)}`,
                    },
                },
                outlined: {
                    '&:hover': {
                        backgroundColor: alpha(colors.teal, 0.06),
                    },
                },
                text: {
                    '&:hover': {
                        backgroundColor: alpha(colors.teal, 0.06),
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${mode === 'dark' ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.06)}`,
                },
                // Ölü temadan taşınan başlık stili: uppercase / gri / küçük / harf aralıklı.
                head: {
                    color: mode === 'dark' ? colors.gray2 : colors.gray,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    // Sticky header güvenliği için yeterince opak (alttan satır sızmasın).
                    backgroundColor: mode === 'dark'
                        ? alpha('#0f172a', 0.96)
                        : alpha(colors.gray6, 0.96),
                    whiteSpace: 'nowrap',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    // Yalnızca gövde satırlarına hafif hover (başlık/footer hariç).
                    '&:hover:not(.MuiTableRow-head):not(.MuiTableRow-footer)': {
                        backgroundColor: mode === 'dark'
                            ? alpha('#FFFFFF', 0.04)
                            : alpha(colors.teal, 0.04),
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    ...getGlassEffect(mode),
                    backgroundColor: 'transparent', // Override MUI default
                    color: mode === 'dark' ? '#f8fafc' : '#1a1a1a',
                    boxShadow: 'none',
                    borderBottom: `1px solid ${mode === 'dark' ? alpha('#FFFFFF', 0.1) : alpha('#000000', 0.05)}`,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    ...getGlassEffect(mode),
                    backdropFilter: 'blur(30px)', // Stronger blur for sidebar
                    borderRight: `1px solid ${mode === 'dark' ? alpha('#FFFFFF', 0.1) : alpha('#000000', 0.05)}`,
                },
            },
        },
    },
});
